import { useEffect, useRef, useState } from 'react';
import { Icon } from '@/components/Icon';
import { formatDate } from '@/lib/clientFormat';

const WEEKDAY_LABELS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const MONTH_LABELS = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];

/** Сетка дней месяца, неделя с понедельника — null там, где ячейка пустая (до 1-го/после последнего числа). */
function buildCalendarGrid(year: number, month: number): (number | null)[][] {
  const firstWeekday = (new Date(year, month, 1).getDay() + 6) % 7; // 0 = понедельник
  const daysTotal = new Date(year, month + 1, 0).getDate();
  const cells: (number | null)[] = [
    ...Array<null>(firstWeekday).fill(null),
    ...Array.from({ length: daysTotal }, (_, i) => i + 1),
  ];
  while (cells.length % 7 !== 0) cells.push(null);
  const weeks: (number | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

/**
 * Замена нативного <input type="date"> — его выпадающий календарь целиком
 * рисует браузер/ОС и никак не стилизуется под дизайн. Тот же визуальный
 * язык, что у CustomSelect/TutorsMultiSelect (кругло, синяя подсветка).
 */
export function CustomDatePicker({
  value,
  onChange,
  placeholder = 'Не выбрано',
  clearable = true,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /**
   * Показывать ли "Очистить (бессрочно)". У клиента дата отключения кабинета
   * действительно может быть бессрочной, а у объявления период показа
   * обязателен — там пункт скрывается (см. AdForm).
   */
  clearable?: boolean;
}) {
  const [open, setOpen] = useState(false);
  // Поле "Дата отключения" обычно в самом низу карточки — если под ним меньше
  // высоты календаря (~330px), открываем вверх, иначе панель уезжает за
  // нижний край экрана и её не видно без скролла.
  const [openUpward, setOpenUpward] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const selectedDate = value ? new Date(`${value}T00:00:00`) : null;
  const [viewDate, setViewDate] = useState(() => selectedDate ?? new Date());

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  const openPicker = () => {
    setViewDate(selectedDate ?? new Date());
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setOpenUpward(window.innerHeight - rect.bottom < 330);
    }
    setOpen((v) => !v);
  };

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const weeks = buildCalendarGrid(year, month);
  const today = new Date();

  const selectDay = (day: number) => {
    onChange(`${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`);
    setOpen(false);
  };

  const isSelected = (day: number) =>
    !!selectedDate &&
    selectedDate.getFullYear() === year &&
    selectedDate.getMonth() === month &&
    selectedDate.getDate() === day;

  const isToday = (day: number) =>
    today.getFullYear() === year && today.getMonth() === month && today.getDate() === day;

  return (
    <div className="datepicker" ref={containerRef}>
      <button type="button" className="multiselect__control" onClick={openPicker}>
        {value ? <span>{formatDate(value)}</span> : <span className="multiselect__placeholder">{placeholder}</span>}
        <Icon name="calendar" size={14} className="multiselect__arrow" />
      </button>

      {open && (
        <div className={`datepicker__dropdown${openUpward ? ' datepicker__dropdown--up' : ''}`}>
          <div className="datepicker__nav">
            <button
              type="button"
              aria-label="Предыдущий месяц"
              onClick={() => setViewDate(new Date(year, month - 1, 1))}
            >
              <Icon name="chevronLeft" size={13} />
            </button>
            <span className="datepicker__month">
              {MONTH_LABELS[month]} {year}
            </span>
            <button
              type="button"
              aria-label="Следующий месяц"
              onClick={() => setViewDate(new Date(year, month + 1, 1))}
            >
              <Icon name="chevronRight" size={13} />
            </button>
          </div>

          <div className="datepicker__weekdays">
            {WEEKDAY_LABELS.map((w) => (
              <span key={w}>{w}</span>
            ))}
          </div>

          <div className="datepicker__grid">
            {weeks.map((week, wi) =>
              week.map((day, di) => (
                <button
                  key={`${wi}-${di}`}
                  type="button"
                  disabled={day === null}
                  className={[
                    'datepicker__day',
                    day === null && 'datepicker__day--empty',
                    day !== null && isSelected(day) && 'datepicker__day--selected',
                    day !== null && isToday(day) && 'datepicker__day--today',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => day !== null && selectDay(day)}
                >
                  {day ?? ''}
                </button>
              )),
            )}
          </div>

          {value && clearable && (
            <button
              type="button"
              className="datepicker__clear"
              onClick={() => {
                onChange('');
                setOpen(false);
              }}
            >
              Очистить (бессрочно)
            </button>
          )}
        </div>
      )}
    </div>
  );
}
